import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { Names, Values } from '@mode-switch/model';
import { EncField, EncSeries, Encoding, EncodingResult, VariableType } from '@mode-vegas/vegas';
import { ChartFacade, DragFieldInfo, SelectedChartService } from '@mode/charts/contract';
import { EncodingHelpers } from '@mode/charts/util-vegas';
import { BinSizeTypes, ChartTypes, FlamingoTypes } from '@mode/shared/contract-common';
import { CompiledAttr } from '@mode/shared/util-switch';
import { getFormatMapKey } from 'libs/charts/util-vegas/src/lib/field-format-helpers';
import { capitalize } from 'lodash';
import { DndDropEvent, DndModule } from 'ngx-drag-drop';
import { Subject, takeUntil } from 'rxjs';
import { EncodingFieldAllComponent } from '../../encoding-field-all/encoding-field-all.component';
import { EncodingFieldMeasureNamesComponent } from '../../encoding-field-measure-names/encoding-field-measure-names.component';
import { EncodingFieldMultipleComponent } from '../../encoding-field-multiple/encoding-field-multiple.component';
import { EncodingFieldSingleComponent } from '../../encoding-field-single/encoding-field-single.component';
import { TypeaheadAttrsListComponent } from '../../typeahead-attrs-list/typeahead-attrs-list.component';
import * as MarksHelpers from '../marks-helpers';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    DndModule,
    DragDropModule,
    EncodingFieldAllComponent,
    EncodingFieldMeasureNamesComponent,
    EncodingFieldMultipleComponent,
    EncodingFieldSingleComponent,
    TypeaheadAttrsListComponent,
  ],
  selector: 'mode-channel-drop-zone',
  templateUrl: './channel-drop-zone.component.html',
  styleUrls: ['./channel-drop-zone.component.scss'],
})
export class ChannelDropZoneComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) channelSummary!: MarksHelpers.ChannelSummary;
  @Input({ required: true }) encoding!: Encoding;
  @Input({ required: true }) model!: FlamingoTypes.ModeModel;
  @Input({ required: true }) view!: FlamingoTypes.ModeView;
  @Input({ required: true }) chartType!: ChartTypes.ChartType;
  @Input({ required: true }) chartService!: SelectedChartService;
  @Input() series: EncSeries | false = false;
  @Input() queryToken: string | undefined;
  @Input() vizToken: string | undefined;
  @Input() allowScalars = true;
  @Input() allowAggregates = true;
  @Input() allowDiscrete = true;
  @Input() allowContinuous = true;
  @Input() allowBinned = true;
  @Input() requiresContinuous = false;
  @Input() showColorSettings = false;
  @Input() fieldFormatMap: ChartTypes.FieldFormatMap | undefined;
  @Input() format: ChartTypes.ChartFormat | undefined;
  @Input() binSizeToFieldMap: BinSizeTypes.BinSizeToFieldMap | undefined;
  @Input() useMeasureNamesPill = false;

  @Output() dragStart = new EventEmitter();
  @Output() dragEnd = new EventEmitter();
  @Output() dropOnField = new EventEmitter<DndDropEvent>();
  @Output() dropOnEmpty = new EventEmitter<DndDropEvent>();
  @Output() fieldUpdated = new EventEmitter<{ $result: EncodingResult; $formats?: ChartTypes.FieldFormatMap }>();
  @Output() removeField = new EventEmitter<{ $field: EncField | string[] }>();
  @Output() removeMultiple = new EventEmitter();
  @Output() removeSort = new EventEmitter<string>();
  @Output() setLegendSplit = new EventEmitter<boolean>();
  @Output() fieldValidationChange = new EventEmitter<{ isValid: boolean; field: EncField }>();
  @Output() typeaheadSelection = new EventEmitter<{
    attr: CompiledAttr;
    event: any;
    onFinalize: () => void;
  }>();
  @Output() getBinSize = new EventEmitter<{ columnName: string; token: string }>();

  disableDrop = false;
  hasSplitLegend = false;
  canLegendBeSplit = false;
  multipleTooltip = '';
  isDragging = false;
  showMeasureNamesPill = false;

  fieldIds: string[] = [];

  dragInfo: DragFieldInfo | undefined;
  private destroyed$ = new Subject<void>();

  // TODO: We might need a way to set the placeholder text externally. Product/Design is asking
  // for more explicit wording for add vs replace.

  constructor(private chartFacade: ChartFacade) {
    this.chartFacade.dragField$.pipe(takeUntil(this.destroyed$)).subscribe((dragInfo: DragFieldInfo) => {
      this.dragInfo = dragInfo;
      this.disableDrop = this.isDisallowedField();
      this.isDragging = this.dragInfo?.formula != null;
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    const measureValuesKey = getFormatMapKey(Values.Key, VariableType.CONTINUOUS);
    this.hasSplitLegend = this.fieldFormatMap?.[measureValuesKey]?.legendFormat?.splitLegend ?? false;

    this.canLegendBeSplit = EncodingHelpers.canSplitLegends(this.encoding, this.series ? this.series.field : undefined);

    const properChannel = capitalize(this.channelSummary.channel);
    this.multipleTooltip =
      `There are multiple fields in ${properChannel} channels across layers. ` +
      `${properChannel} settings will be controlled by the field in each layer.`;

    this.fieldIds = [this.channelSummary.id ?? ''];

    this.showMeasureNamesPill = this.useMeasureNamesPill && this.channelSummary.field?.formula === Names.Key;
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  onDragStart(event: DragEvent) {
    this.dragStart.emit();
  }

  onDragEnd(event: DragEvent) {
    this.dragEnd.emit();
  }

  onFieldDrop(event: DndDropEvent) {
    this.dropOnField.emit(event);
  }

  onEmptyFieldDrop(event: DndDropEvent) {
    this.dropOnEmpty.emit(event);
  }

  onTypeAheadSelection($event: { attr: CompiledAttr; event: any; onFinalize: () => void }) {
    this.typeaheadSelection.emit($event);
  }

  onDropOnMeasureNames() {
    this.chartFacade.endFieldDrag();
  }

  isDisallowedField(): boolean {
    const dragField: DragFieldInfo | undefined = this.dragInfo;
    if (!dragField) {
      return false;
    }

    if (dragField.formula == null && dragField.groupId != null) {
      return true;
    }

    if (this.requiresContinuous && dragField.formula != null) {
      // Disallow if this can NOT be made into a continuous measure
      return !EncodingHelpers.canBeContinuousMeasure(dragField.formula, true);
    }

    return false;
  }

  onFieldUpdated($event: { $result: EncodingResult; $formats?: ChartTypes.FieldFormatMap }) {
    this.fieldUpdated.emit($event);
  }

  onRemoveField($event: { $field: EncField | string[] }) {
    this.removeField.emit($event);
  }

  onRemoveSort($event: { $source: string }) {
    this.removeSort.emit($event.$source);
  }

  onSetLegendSplit(isSplit: boolean): void {
    this.setLegendSplit.emit(isSplit);
  }

  onIsValidField(event$: { isValid: boolean; field: EncField }) {
    this.fieldValidationChange.emit(event$);
  }

  onRemoveMultiple() {
    this.removeMultiple.emit();
  }

  onGetBinSize($event: { columnName: string; token: string }) {
    this.getBinSize.emit($event);
  }
}
